import React from 'react';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import { rgba, lighten } from 'polished';
import theme, { textStyles as ts } from '../../theme';
import Spinner from '../Spinner';

const mobileStyle = css`
  @media (max-width: ${theme('layout.pageWidth')}) {
    max-width: 100%;
    width: 100%;
    flex: auto;
  }
`;

const buttonBaseStyle = css`
  ${ts.button};
  box-sizing: border-box;
  border-radius: ${theme('radius.regular')};
  height: ${theme('size.button')};
  border: 1px solid ${theme('colors.accentTertiary')};
  padding: 0 ${theme('spacing.regular')};
  justify-content: center;
  align-items: center;
  cursor: pointer;
  outline: 0 none;
  transition: background-color 0.1s linear, color 0.1s linear,
    border-color 0.1s linear, box-shadow 0.1s linear, transform 0.1s linear;
  ${mobileStyle};
`;

const Button = styled.button`
  ${buttonBaseStyle};
  display: flex;
  ${theme('size.buttonWidth').apply(
    (w, { dynamicWidth }) => (dynamicWidth ? '' : `flex: 0 1 ${w}; width: ${w}`),
  )};
`;

const Link = styled.a`
  ${buttonBaseStyle};
  text-decoration: none;
  text-align: center;
  display: flex;
  align-items: center;
  gap: ${theme('spacing.regular')};
  ${theme('size.buttonWidth').apply(
    (w, { dynamicWidth }) => (dynamicWidth ? '' : `flex: 0 1 ${w}; width: ${w}`),
  )};
`;

const Anchor = styled.a`
  text-decoration: none;
  text-align: center;
  display: flex;
  align-items: center;
  gap: ${theme('spacing.regular')};
  color: ${theme('colors.accentQuarternary')};
  font-weight: 600;
  ${theme('size.buttonWidth').apply(
    (w, { dynamicWidth }) => (dynamicWidth ? '' : `flex: 0 1 ${w}; width: ${w}`),
  )};
`;

const secondaryButtonStyle = css`
  background-color: transparent;
  color: ${theme('colors.textPrimary')};

  &:hover {
    background-color: ${theme('colors.accentTertiary').apply(col => lighten(0.2, col))};
    border-color: ${theme('colors.accentTertiary').apply(col => lighten(0.2, col))};
  }
`;

const outlineButtonStyle = css`
  background-color: transparent;
  color: ${theme('colors.textPrimary')};
  border-color: white;

  &:hover {
    border-color: ${rgba('#ffffff', 0.5)};
    color: ${rgba('#ffffff', 0.5)};
  }
`;

const primaryButtonStyle = css`
  background-color: ${theme('colors.accentTertiary')};
  color: ${theme('colors.textPrimary')};

  &:hover {
    border-color: ${theme('colors.accentTertiary').apply(col => lighten(0.2, col))};
    background-color: ${theme('colors.accentTertiary').apply(col => lighten(0.2, col))};
  }
`;

const ButtonSecondary = styled(Button)`
  ${secondaryButtonStyle};
`;

const ButtonOutline = styled(Button)`
  ${outlineButtonStyle};
`;

const ButtonPrimary = styled(Button)`
  ${primaryButtonStyle};
`;

const ButtonBiggerPrimary = styled(Button)`
  ${primaryButtonStyle};
  width: ${theme('size.buttonWidthBigger')};
  flex-basis: auto;
`;

const LinkSecondary = styled(Link)`
  ${secondaryButtonStyle};
`;

const LinkBiggerSecondary = styled(Link)`
  ${secondaryButtonStyle};
  width: ${theme('size.buttonWidthBigger')};
  flex-basis: auto;
  border: 2px solid ${theme('colors.haleDark')};
`;

const LinkOutline = styled(Link)`
  ${outlineButtonStyle};
`;

const LinkPrimary = styled(Link)`
  ${primaryButtonStyle};
`;

const ButtonOutlineSecondary = styled(Link)`
  background-color: transparent;
  color: ${theme('colors.haleDark')};
  border-color: ${theme('colors.haleDark')};
  gap: ${theme('spacing.regular')};
  height: ${theme('size.buttonSmall')};
  max-width: ${theme('size.buttonWidthSmall')};
`;

export const PrimaryButton = ({
  title,
  loading,
  onClick,
  formAction,
  href,
  icon,
  bigger,
  ...props
}) => {
  let Elem;

  if (href) {
    Elem = LinkPrimary;
  } else if (bigger) {
    Elem = ButtonBiggerPrimary;
  } else {
    Elem = ButtonPrimary;
  }
  
  const elemProps = href
    ? {
      href,
    } : {
      formAction,
    };
  return (
    <Elem
      onClick={onClick}
      disabled={loading}
      bigger={bigger}
      {...elemProps}
      {...props}
    >
      {loading ? <Spinner /> : title}
      {icon}
     
    </Elem>
  );
};
PrimaryButton.propTypes = {
  title: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  width: PropTypes.number,
  formAction: PropTypes.string,
  href: PropTypes.string,
  onClick: PropTypes.func,
  dynamicWidth: PropTypes.bool,
  icon: PropTypes.node,
  bigger: PropTypes.bool
};
PrimaryButton.defaultProps = {
  loading: false,
  width: 0,
  href: null,
  formAction: null,
  onClick: () => {},
  dynamicWidth: false,
  icon: null,
  bigger: false
};

export const SecondaryButton = ({
  title,
  onClick,
  formAction,
  href,
  icon,
  bigger,
  ...props
}) => {

  let Elem;
  if (href) {
    if(bigger) {
      Elem = LinkBiggerSecondary;
    } else {
      Elem = LinkSecondary;
    }
  } else {
    Elem = ButtonSecondary;
  }
  const elemProps = href
    ? {
      href,
    } : {
      formAction,
    };
  return (
    <Elem onClick={onClick} {...elemProps} {...props}>
      {title}
    </Elem>
  );
};
SecondaryButton.propTypes = {
  title: PropTypes.string.isRequired,
  width: PropTypes.number,
  formAction: PropTypes.string,
  onClick: PropTypes.func,
  dynamicWidth: PropTypes.bool,
  href: PropTypes.string,
  icon: PropTypes.node,
  bigger: PropTypes.bool
};
SecondaryButton.defaultProps = {
  width: 0,
  formAction: null,
  onClick: () => {},
  dynamicWidth: false,
  href: '',
  icon: null,
  bigger: false
};

export const SecondaryAnchorLink = ({
  title,
  onClick,
  formAction,
  href,
  icon,
  ...props
}) => {
  const Elem = href ? Anchor : ButtonSecondary;
  const elemProps = href
    ? {
      href,
    } : {
      formAction,
    };
  return (
    <Elem onClick={onClick} {...elemProps} {...props}>
      {title}
    </Elem>
  );
};
SecondaryAnchorLink.propTypes = {
  title: PropTypes.string.isRequired,
  width: PropTypes.number,
  formAction: PropTypes.string,
  onClick: PropTypes.func,
  dynamicWidth: PropTypes.bool,
  href: PropTypes.string,
  icon: PropTypes.node,
};
SecondaryAnchorLink.defaultProps = {
  width: 0,
  formAction: null,
  onClick: () => {},
  dynamicWidth: false,
  href: '',
  icon: null,
};


export const OutlineButton = ({
  title,
  onClick,
  formAction,
  href,
  icon,
  ...props
}) => {
  const Elem = href ? LinkOutline : ButtonOutline;
  const elemProps = href
    ? {
      href,
    } : {
      formAction,
    };
  return (
    <Elem
      onClick={onClick}
      {...elemProps}
      {...props}
    >
      {title}
    </Elem>
  );
};
OutlineButton.propTypes = {
  title: PropTypes.string.isRequired,
  width: PropTypes.number,
  formAction: PropTypes.string,
  onClick: PropTypes.func,
  dynamicWidth: PropTypes.bool,
  href: PropTypes.string,
  icon: PropTypes.node,
};
OutlineButton.defaultProps = {
  width: 0,
  formAction: null,
  onClick: () => {},
  dynamicWidth: false,
  href: '',
  icon: null,
};


export const OutlineButtonSecondary = ({
  title,
  onClick,
  formAction,
  href,
  icon,
  ...props
}) => {
  const elemProps = href
    ? {
      href,
    } : {
      formAction,
    };
  return (
    <ButtonOutlineSecondary
      onClick={onClick}
      {...elemProps}
      {...props}
    >
      {icon != null ? icon : null}
      {title}
    </ButtonOutlineSecondary>
  );
};
OutlineButtonSecondary.propTypes = {
  title: PropTypes.string.isRequired,
  width: PropTypes.number,
  formAction: PropTypes.string,
  onClick: PropTypes.func,
  dynamicWidth: PropTypes.bool,
  href: PropTypes.string,
  icon: PropTypes.node,
};
OutlineButtonSecondary.defaultProps = {
  width: 0,
  formAction: null,
  onClick: () => {},
  dynamicWidth: false,
  href: '',
  icon: null,
};